import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Plus, Search, ShieldCheck, Cloud, Server, MoreVertical, Pencil, RefreshCw, Trash2, ChevronRight } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { PageHeader, StatusBadge, DataTable, AsyncBoundary, RowsSkeleton, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CreateResourceDialog } from "@/components/common/CreateResourceDialog";

export default function SitesDomains() {
  const { data, loading, error, reload } = useAsync(() => api.getDomains());
  const [q, setQ] = useState("");
  const rows = useMemo(() => (data || []).filter((d) => !q || `${d.domain} ${d.project} ${d.client}`.toLowerCase().includes(q.toLowerCase())), [data, q]);

  const stats = data ? {
    total: data.length,
    healthy: data.filter((d) => d.status === "healthy").length,
    ssl: data.filter((d) => d.sslStatus === "assigned").length,
    issues: data.filter((d) => d.missingSince || d.proxy === "disabled").length,
  } : {};

  const columns = [
    { key: "domain", header: "Domínio", render: (r) => (
      <div><Link to={`/dominios/${r.domain}`} className="inline-flex items-center gap-1 font-mono text-sm text-foreground hover:text-brand" data-testid={`domain-link-${r.domain}`}>{r.domain}<ChevronRight className="h-3 w-3 opacity-50" /></Link><p className="text-[11px] text-muted-foreground">{r.project} · {r.client}</p></div>
    ) },
    { key: "server", header: "Servidor", render: (r) => <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground"><Server className="h-3 w-3" />{r.server}</span> },
    { key: "proxy", header: "Proxy", render: (r) => <span className="font-mono text-[11px] text-muted-foreground">{r.proxy}</span> },
    { key: "dns", header: "DNS", render: (r) => <span className="inline-flex items-center gap-1 text-xs capitalize">{r.dns === "cloudflare" && <Cloud className="h-3 w-3 text-info" />}{r.dns}</span> },
    { key: "ssl", header: "SSL", render: (r) => (
      r.sslStatus === "unknown" ? <span className="text-muted-foreground text-xs">Unknown</span> : r.sslStatus === "assigned" ? <span className="inline-flex items-center gap-1 text-xs text-info"><ShieldCheck className="h-3.5 w-3.5" />Certificado atribuído</span>
      : <span className="text-muted-foreground text-xs">Sem certificado observado</span>
    ) },
    { key: "expires", header: "Expira", render: (r) => <span className="text-xs text-muted-foreground">{r.expires}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} size="sm" /> },
    { key: "actions", header: "", align: "right", render: (r) => (
      <span className="text-xs text-muted-foreground">Somente leitura</span>
    ) },
  ];

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader
        icon={Globe}
        title="Sites & Domínios"
        description="Sites cadastrados e domínio principal. DNS, SSL e provisionamento ainda não integrados."
        breadcrumb={<><span>Aplicações</span><span>/</span><span className="text-foreground">Sites & Domínios</span></>}
        actions={<><CreateResourceDialog kind="sites" onCreated={reload} /></>}
      />

      {!loading && data && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard icon={Globe} label="Domínios" value={String(stats.total)} accent />
          <StatCard icon={ShieldCheck} label="Certificado atribuído" value={String(stats.ssl)} dots={[{ tone: "info", label: "validade não verificada" }]} />
          <StatCard icon={Globe} label="Healthy" value={String(stats.healthy)} dots={stats.healthy > 0 ? [{ tone: "ok", label: "operacionais" }] : undefined} />
          <StatCard icon={Globe} label="Ausentes/desativados" value={String(stats.issues)} dots={stats.issues > 0 ? [{ tone: "err", label: "observação NPM" }] : undefined} />
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar domínio..." data-testid="domains-search" className="border-line bg-surface pl-9" />
      </div>

      <AsyncBoundary loading={loading} error={error} skeleton={<RowsSkeleton rows={8} />}>
        <DataTable columns={columns} rows={rows} keyField="domain" testid="domains-table" />
      </AsyncBoundary>
    </div>
  );
}
